import React from "react";
import { action, computed, observable } from "mobx";
import Grid from "@material-ui/core/Grid";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Table from "@material-ui/core/Table";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import TableBody from "@material-ui/core/TableBody";
import Button from "@material-ui/core/Button";
import ArrowBackIosIcon from "@material-ui/icons/ArrowBackIos";
import Skeleton from "@material-ui/lab/Skeleton";
import { Pie, PieChart, ResponsiveContainer, Cell, Sector } from "recharts";

import BaseScenarioDashboardView from "../BaseScenarioDashboardView";
import Header from "../../components/utils/Header";
import WorldMapSvg from "../../components/map/WorldMapSvg";
import { statGreen, statGrey, statOrange, statRed } from "../../globals";
import { autoFormatUnit } from "../../utils";
import ScenarioSelect from "../scenarios/ScenarioSelect";
import ConditionalFormat from "../../components/utils/ConditionalFormat";
import FilterListView from "../../components/FilterListView";
import DemandSwitchesPanel from "../../components/DemandSwitchesPanel";
import DemandSwitches from "../../store/DemandSwitches";
import MapMarker from "../../components/map/MapMarker";
import conditionalFormats from "../../store/conditionalFormats";

export const styles = (theme) => ({
  sidebarPaper: {
    padding: theme.spacing(2),
    minWidth: 325,
  },
  pieContent: {
    height: "100%",
  },
  green: {
    fill: statGreen,
  },
  orange: {
    fill: statOrange,
  },
  red: {
    fill: statRed,
  },
  grey: {
    fill: statGrey,
  },
  back: {
    cursor: "pointer",
  },
  backPaper: {
    padding: theme.spacing(2),
  },
});

class BaseMapView extends BaseScenarioDashboardView {
  static apiSubPath = "/demand_map/";

  title = "Map";
  markerVariant = "circle";

  @observable scenario = null;
  @observable year = null;
  @observable kpi = "delta_last_cycle";
  @observable unit = "volume";
  @observable granularity = "technology";
  @observable region = null;
  @observable activePieIndex = null;
  @observable seeMore = false;

  constructor(props) {
    super(props);
    this.createSwitches();
  }

  createSwitches() {
    this.switches = new DemandSwitches({}, this.updateData);
  }

  setSwitches(data) {
    if (this.switches !== null) {
      this.switches.set("Base demand in scope", data.base_demand_in_scope);
      this.switches.set("Base demand out of scope", data.base_demand_out_scope);
      this.switches.set("Innovation in scope", data.innovation_demand);
      this.switches.set("Risks & opps in scope", data.risks_opps_demand);
    }
  }

  _setData(data) {
    this.scenario = data.scenario;
    this.year = data.year;
    this.setSwitches(data);
  }

  getSwitches() {
    if (this.switches === null) {
      return {};
    }
    return {
      base_demand_in_scope: this.switches.get("Base demand in scope"),
      base_demand_out_scope: this.switches.get("Base demand out of scope"),
      innovation_demand: this.switches.get("Innovation in scope"),
      risks_opps_demand: this.switches.get("Risks & opps in scope"),
    };
  }

  _getQueryParams() {
    return {
      scenario: this.scenario,
      year: this.year,
      ...this.getSwitches(),
    };
  }

  beforeLocationChange() {
    this.seeMore = false;
  }

  get kpiChoices() {
    return [
      { value: "delta_last_cycle", display_name: "Delta vs Last Cycle" },
      { value: "delta_last_year", display_name: "Delta vs Y-1" },
    ];
  }

  get unitChoices() {
    return [
      { value: "volume", display_name: "t" },
      { value: "value", display_name: "M€" },
    ];
  }

  get granularityChoices() {
    return [{ value: "technology", display_name: "Technology" }];
  }

  isRatioKpi(kpiName) {
    return false;
  }

  @computed get allKpiKeys() {
    const names = [
      "delta_last_year_ratio_volume",
      "delta_last_cycle_ratio_volume",
    ];
    for (const { value: kpiName } of this.kpiChoices) {
      if (this.isRatioKpi(kpiName)) {
        names.push(kpiName);
      } else {
        for (const { value: unitName } of this.unitChoices) {
          names.push(`${kpiName}_${unitName}`);
        }
      }
    }
    return names;
  }

  @action onRegionChange = (regionName) => {
    this.region = regionName;
  };

  @action onKpiChange = (event) => {
    this.kpi = event.target.value;
  };

  @action onUnitChange = (event) => {
    this.unit = event.target.value;
  };

  @action onGranularityChange = (event) => {
    this.granularity = event.target.value;
  };

  onScenarioChange = this.genericChangeHandler("scenario");
  onYearChange = this.genericChangeHandler("year");
  onSeeMore = this.genericToggleHandler("seeMore", false);

  get granularityName() {
    if (this.region === null) {
      return "Macro Zone";
    }
    return this.getVerbose(this.granularityChoices, this.granularity);
  }

  get verboseUnit() {
    if (this.isRatioKpi(this.kpi)) {
      return "%";
    }
    return this.getVerbose(this.unitChoices, this.unit);
  }

  get granularTable() {
    if (this.region === null) {
      return this.data.region_table;
    }
    if (this.granularity === "cbu") {
      return this.data.cbu_table;
    }
    return this.data.techno_table;
  }

  get extraControls() {
    return null;
  }

  get controls() {
    if (!this.initialized) {
      return <Skeleton variant="rect" width={300} height={55} />;
    }
    return (
      <Grid container spacing={1} justify="flex-end" alignItems="center">
        <Grid item>
          <ScenarioSelect
            scenarioId={this.scenario}
            choices={this.data.scenario_choices}
            onScenarioChange={this.onScenarioChange}
          />
        </Grid>
        {this.extraControls}
        <Grid item>
          {this.renderChoicesField(
            "Year",
            this.data.year_choices,
            this.year,
            this.onYearChange
          )}
        </Grid>
        <Grid item>
          {this.renderChoicesField(
            "KPI",
            this.kpiChoices,
            this.kpi,
            this.onKpiChange
          )}
        </Grid>
        {this.isRatioKpi(this.kpi) ? null : (
          <Grid item>
            {this.renderChoicesField(
              "Unit",
              this.unitChoices,
              this.unit,
              this.onUnitChange
            )}
          </Grid>
        )}
        <Grid item>
          {this.renderChoicesField(
            "Granularity",
            this.granularityChoices,
            this.granularity,
            this.onGranularityChange
          )}
        </Grid>
        <Grid item>
          <FilterListView filterList={this.filterList} />
        </Grid>
      </Grid>
    );
  }

  @computed get allValues() {
    return this.data.region_table.map((row) => Math.abs(this.getValue(row)));
  }

  @computed get minValue() {
    return Math.min(...this.allValues);
  }

  @computed get maxValue() {
    return Math.max(...this.allValues);
  }

  isGreyKpi(kpiKey) {
    return false;
  }

  getConditionalFormatValue(kpiKey, row, forRegions = false) {
    if (kpiKey.startsWith("delta_last_cycle_")) {
      kpiKey = "delta_last_cycle_ratio_volume";
    } else if (kpiKey.startsWith("delta_last_year_")) {
      kpiKey = "delta_last_year_ratio_volume";
    }
    const value = row[kpiKey];
    const conditionalFormatName = this.getConditionalFormatName(
      kpiKey,
      forRegions
    );
    if (
      conditionalFormatName !== "map-default" &&
      conditionalFormatName !== "map-stock-alerts"
    ) {
      return value;
    }
    const values = (
      forRegions ? this.data.region_table : this.granularTable
    ).map((row) => row[kpiKey]);
    const min = Math.min(...values);
    const max = Math.max(...values);
    return (value - min) / (max - min);
  }

  getConditionalFormatName(kpiKey, forRegions = false) {
    if (this.isGreyKpi(kpiKey)) {
      return null;
    }
    if (
      kpiKey.startsWith("below_min_stock_") ||
      kpiKey.startsWith("above_max_stock_")
    ) {
      return "map-stock-alerts";
    }
    if (this.isRatioKpi(kpiKey)) {
      return "map-ratio";
    }
    if (kpiKey.startsWith("delta_last_cycle_")) {
      return forRegions
        ? "map-delta-last-cycle-worldwide"
        : "map-delta-last-cycle";
    }
    if (kpiKey.startsWith("delta_last_year_")) {
      return forRegions
        ? "map-delta-last-year-worldwide"
        : "map-delta-last-year";
    }
    if (kpiKey === "quantity_volume" || kpiKey === "quantity_value") {
      return "quantity-kpi";
    }
    return "map-default";
  }

  getColorClassName = (kpiKey, row, forRegions = false) => {
    if (this.isGreyKpi(kpiKey)) {
      return "grey";
    }
    const value = this.getConditionalFormatValue(kpiKey, row, forRegions);
    const conditionalFormat =
      conditionalFormats[this.getConditionalFormatName(kpiKey, forRegions)];
    if (conditionalFormat) {
      return conditionalFormat.getColorName(value);
    }
    return "grey";
  };

  getMarkerSubtitle(kpiKey, row) {
    let value = null;
    if (kpiKey.startsWith("delta_last_cycle_")) {
      value = row.delta_last_cycle_ratio_volume;
    }
    if (kpiKey.startsWith("delta_last_year_")) {
      value = row.delta_last_year_ratio_volume;
    }
    if (value === null) {
      return null;
    }
    value -= 1; // - 1 to get a delta around 0.
    return `(${this.formatValue(value, "%")})`;
  }

  get map() {
    if (!this.ready) {
      return <Skeleton variant="rect" height={450} />;
    }
    return (
      <WorldMapSvg onBackgroundClick={() => this.onRegionChange(null)}>
        {this.data.region_table.map((row) => (
          <MapMarker
            variant={this.markerVariant}
            key={row.name}
            latitude={row.latitude}
            longitude={row.longitude}
            value={this.getValue(row)}
            subtitle={this.getMarkerSubtitle(this.kpiKey, row)}
            active={row.name === this.region}
            minValue={this.minValue}
            maxValue={this.maxValue}
            unit={this.verboseUnit}
            className={this.getColorClassName(this.kpiKey, row, true)}
            onClick={() => this.onRegionChange(row.name)}
          />
        ))}
      </WorldMapSvg>
    );
  }

  onPieEnter = (data, index) => {
    this.activePieIndex = index;
  };

  onPieLeave = () => {
    this.activePieIndex = null;
  };

  get kpiKey() {
    if (this.isRatioKpi(this.kpi)) {
      return this.kpi;
    }
    return `${this.kpi}_${this.unit}`;
  }

  getValue = (row) => {
    return row[this.kpiKey];
  };

  formatValue = (value, unit = null) => {
    if (unit === null) {
      unit = this.verboseUnit;
    }
    return autoFormatUnit(value, unit);
  };

  @computed get regionRow() {
    if (this.region === null) {
      const row = { name: "Worldwide" };
      for (const kpiKey of this.allKpiKeys) {
        const values = this.data.region_table.map((row) => row[kpiKey]);
        row[kpiKey] = values.reduce((sum, v) => sum + v);
        if (
          this.isRatioKpi(kpiKey) ||
          kpiKey === "delta_last_year_ratio_volume" ||
          kpiKey === "delta_last_cycle_ratio_volume"
        ) {
          row[kpiKey] /= values.length;
        }
      }
      return row;
    }
    return this.data.region_table.filter((row) => row.name === this.region)[0];
  }

  @computed get selectedRows() {
    if (this.region === null) {
      return [...this.data.region_table];
    }
    return this.granularTable.filter((row) => row.Region === this.region);
  }

  @computed get absSelectedRows() {
    return this.selectedRows.map((row) => {
      const absRow = { ...row };
      for (const kpiKey of this.allKpiKeys) {
        absRow[kpiKey] = Math.abs(absRow[kpiKey]);
      }
      return absRow;
    });
  }

  renderKpi(row, kpiKey, forRegions = false) {
    const kpiKeyParts = kpiKey.split("_");
    let unit;
    if (this.isRatioKpi(kpiKey)) {
      unit = "%";
    } else {
      const unitValue = kpiKeyParts[kpiKeyParts.length - 1];
      unit = this.getVerbose(this.unitChoices, unitValue);
    }
    return (
      <ConditionalFormat
        value={this.getConditionalFormatValue(kpiKey, row, forRegions)}
        name={this.getConditionalFormatName(kpiKey, forRegions)}
        variant="rect"
      >
        {this.formatValue(row[kpiKey], unit)}
      </ConditionalFormat>
    );
  }

  get info() {
    if (!this.ready) {
      return <Skeleton variant="rect" width={330} height={450} />;
    }
    const { classes } = this.props;
    const getActiveShape = (props) => {
      return (
        <g>
          <Sector {...props} />
          <Sector
            {...{
              ...props,
              innerRadius: 135,
              outerRadius: 139,
            }}
          />
        </g>
      );
    };
    const renderLabel = (props) => {
      const { cx, cy, innerRadius, index } = props;
      if (index !== 0) {
        return null;
      }
      const activeRow =
        this.activePieIndex === null ||
        this.activePieIndex >= this.selectedRows.length
          ? this.regionRow
          : this.selectedRows[this.activePieIndex];
      const size = 2 * Math.sqrt(innerRadius ** 2 / 2);
      return (
        <foreignObject
          x={cx - size / 2}
          y={cy - size / 2}
          width={size}
          height={size}
        >
          <Grid
            container
            direction="column"
            justify="center"
            alignItems="center"
            className={classes.pieContent}
          >
            <Grid item>
              <Typography variant="subtitle1">{activeRow.name}</Typography>
            </Grid>
            <Grid item>
              <Typography variant="h5">
                {this.formatValue(this.getValue(activeRow))}
              </Typography>
              <Typography variant="h6" align="center">
                {this.getMarkerSubtitle(this.kpiKey, activeRow)}
              </Typography>
            </Grid>
          </Grid>
        </foreignObject>
      );
    };
    return (
      <Paper className={classes.sidebarPaper}>
        <Grid container direction="column" wrap="nowrap" spacing={1}>
          <Grid item>
            <Typography variant="h5">Information</Typography>
            <Typography variant="subtitle2">{this.regionRow.name}</Typography>
          </Grid>
          <Grid item>
            <ResponsiveContainer aspect={1}>
              <PieChart>
                <Pie
                  activeIndex={this.activePieIndex}
                  activeShape={getActiveShape}
                  labelLine={false}
                  label={renderLabel}
                  innerRadius={110}
                  outerRadius={130}
                  startAngle={450}
                  endAngle={90}
                  paddingAngle={2}
                  data={this.absSelectedRows}
                  dataKey={this.kpiKey}
                  onMouseEnter={this.onPieEnter}
                  onMouseLeave={this.onPieLeave}
                  isAnimationActive={false}
                >
                  {this.absSelectedRows.map((row, index) => (
                    <Cell
                      key={`cell-${index}`}
                      className={
                        classes[
                          this.getColorClassName(
                            this.kpiKey,
                            row,
                            this.region === null
                          )
                        ]
                      }
                    />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
          </Grid>
          <Grid item>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{this.granularityName}</TableCell>
                  <TableCell>KPI</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {this.selectedRows.slice(0, 5).map((row) => (
                  <TableRow key={row.name}>
                    <TableCell>
                      <strong>{row.name}</strong>
                    </TableCell>
                    <TableCell>
                      {this.renderKpi(row, this.kpiKey, this.region === null)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={this.onSeeMore}
            >
              See more
            </Button>
          </Grid>
        </Grid>
      </Paper>
    );
  }

  renderSeeMoreRow(row, name = null, forRegions = false) {
    if (name === null) {
      name = row.name;
    }
    return (
      <TableRow key={name}>
        <TableCell>{name}</TableCell>
        {this.kpiChoices.map(({ value: kpiName }) => {
          if (this.isRatioKpi(kpiName)) {
            return (
              <TableCell key={kpiName} align="center">
                {this.renderKpi(row, kpiName, forRegions)}
              </TableCell>
            );
          }
          return this.unitChoices.map(({ value: unitName }) => {
            const key = `${kpiName}_${unitName}`;
            return (
              <TableCell key={key} align="center">
                {this.renderKpi(row, key, forRegions)}
              </TableCell>
            );
          });
        })}
      </TableRow>
    );
  }

  get renderSeeMore() {
    const { classes } = this.props;
    return (
      <Grid container direction="column" spacing={2} wrap="nowrap">
        <Grid item>
          <Typography
            variant="h4"
            onClick={this.onSeeMore}
            className={classes.back}
          >
            <ArrowBackIosIcon color="primary" />
            {this.regionRow.name}
          </Typography>
        </Grid>
        <Grid item>
          <Paper className={classes.backPaper}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>
                    {this.granularityName} ({this.selectedRows.length})
                  </TableCell>
                  {this.kpiChoices.map(
                    ({ value: kpiName, display_name: kpiVerbose }) => (
                      <TableCell
                        key={kpiName}
                        align="center"
                        colSpan={this.isRatioKpi(kpiName) ? 1 : 2}
                      >
                        {kpiVerbose}
                      </TableCell>
                    )
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {this.renderSeeMoreRow(
                  this.regionRow,
                  <strong>{this.regionRow.name}</strong>,
                  true
                )}
                {this.selectedRows.map((row) => this.renderSeeMoreRow(row))}
              </TableBody>
            </Table>
          </Paper>
        </Grid>
      </Grid>
    );
  }

  render() {
    if (this.seeMore && this.initialized) {
      return this.renderSeeMore;
    }
    return (
      <Grid container direction="column" spacing={4} wrap="nowrap">
        <Grid
          item
          container
          spacing={1}
          justify="space-between"
          alignItems="center"
          wrap="nowrap"
        >
          <Grid item>
            <Header title={this.title} />
          </Grid>
          <Grid item>{this.controls}</Grid>
        </Grid>
        <Grid item>
          <Grid container spacing={2} wrap="nowrap">
            <Grid item xs>
              {this.map}
            </Grid>
            <Grid item>
              <Grid container direction="column" spacing={2} wrap="nowrap">
                {this.switches === null ? null : (
                  <Grid item>
                    <DemandSwitchesPanel
                      switches={this.switches}
                      direction="column"
                    />
                  </Grid>
                )}
                <Grid item>{this.info}</Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    );
  }
}

export default BaseMapView;
